import { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, Edit2, Trash2, Send, Image as ImageIcon } from 'lucide-react';
import OverflowMenu from '@/components/shared/OverflowMenu';
import { getImageUrl } from './utils';

// ==================== IMMERSIVE VIEWER ====================
export function PhotoViewer({ photos, index, onClose, onNavigate, isOwner, onEdit, onDelete, onPostcard, collectionId, onSetCover, currentCoverId, onTogglePublic }) {
  const photo = photos[index];
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  useEffect(() => {
    const h = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && index > 0) onNavigate(index - 1);
      if (e.key === 'ArrowRight' && index < photos.length - 1) onNavigate(index + 1);
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [index, photos.length, onClose, onNavigate]);

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe && index < photos.length - 1) {
      onNavigate(index + 1);
    }
    if (isRightSwipe && index > 0) {
      onNavigate(index - 1);
    }
  };

  if (!photo) return null;

  const tagline = photo.tagline || photo.title || photo.caption || '';
  const position = photo.tagline_position || 'below';
  const isCover = currentCoverId === photo.id;

  return (
    <div 
      className="fixed inset-0 bg-[#0a0a0f] z-[100] flex flex-col" 
      data-testid="photo-viewer" 
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {/* Close button — always visible */}
      <div className="absolute inset-x-0 top-0 z-20 flex items-center justify-between px-6 py-4">
        <span className="text-white/50 text-xs font-mono tracking-wider">{index + 1} / {photos.length}</span>
        <div className="flex items-center gap-4">
          {isOwner && (
              <OverflowMenu
                publishToggle={{ isPublished: photo.is_public, onToggle: () => onTogglePublic(photo) }}
                triggerStyle={{ position: 'static' }}
                items={[
                  { icon: <Send size={16} strokeWidth={1.5} />, label: 'Make postcard', onClick: () => onPostcard(photo) },
                  ...(collectionId && onSetCover && !isCover ? [{ icon: <ImageIcon size={16} strokeWidth={1.5} />, label: 'Set as cover', onClick: () => onSetCover(photo.id) }] : []),
                  { icon: <Edit2 size={16} strokeWidth={1.5} />, label: 'Edit', onClick: () => { onClose(); onEdit(photo); } },
                  { icon: <Trash2 size={16} strokeWidth={1.5} />, label: 'Delete', onClick: () => onDelete(photo), destructive: true },
                ]}
              />
          )}
          <button onClick={onClose} className="text-white/70 hover:text-white transition-colors" data-testid="viewer-close">
            <X size={32} />
          </button>
        </div>
      </div>

      {/* Cover badge */}
      {isCover && (
        <div className="absolute top-4 left-6 z-20 px-3 py-1 bg-[#6B35B8] rounded text-xs text-white uppercase tracking-wider">
          Cover Photo
        </div>
      )}

      {/* Navigation arrows - larger and more visible */}
      {index > 0 && (
        <button onClick={() => onNavigate(index - 1)} data-testid="viewer-prev"
          className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-black/20 text-white/40 hover:text-white/80 hover:bg-black/40 transition-all"
        >
          <ChevronLeft size={48} strokeWidth={1.5} />
        </button>
      )}
      {index < photos.length - 1 && (
        <button onClick={() => onNavigate(index + 1)} data-testid="viewer-next"
          className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-black/20 text-white/40 hover:text-white/80 hover:bg-black/40 transition-all"
        >
          <ChevronRight size={48} strokeWidth={1.5} />
        </button>
      )}

      {/* Photo */}
      <div className="flex-1 flex items-center justify-center relative overflow-hidden">
        <img src={getImageUrl(photo.url)} alt="" data-testid="viewer-image"
          className="max-h-full max-w-full object-contain select-none"
          draggable={false}
        />
      </div>

      {/* Bottom bar */}
      {tagline && position !== 'none' && position !== 'superimposed' && (
        <div className="px-6 pb-6 pt-4 text-center">
          <p className="text-white/70 italic" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '18px' }}>{tagline}</p>
          <div className="flex items-center justify-center gap-4 mt-2 text-xs text-white/30" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {photo.location && <span>{photo.location}</span>}
            {photo.date_taken && <span>{photo.date_taken}</span>}
          </div>
        </div>
      )}
    </div>
  );
}
